import type { JSX } from 'react';
import type { WireScheduledPrompt } from '@logistics/contracts';
import { failureFrom } from '@/lib/result';
import { stamp } from '@/lib/format';
import { Modal } from '../shell/modal';
import { ErrorState, LoadingState } from '../shell/states';
import { useScheduleRuns } from './use-schedules';

/** The last runs of one schedule: when, how it went, how many got it, and why not. */
export function ScheduleRuns({
  schedule,
  onClose,
}: {
  schedule: WireScheduledPrompt;
  onClose: () => void;
}): JSX.Element {
  const runs = useScheduleRuns(schedule.id);
  return (
    <Modal eyebrow="RUN HISTORY" title={schedule.name} onClose={onClose}>
      <div className="flex flex-col gap-2 overflow-y-auto px-7 py-6">
        {runs.isPending && <LoadingState rows={3} />}
        {runs.isError && (
          <ErrorState {...failureFrom(runs.error)} onRetry={() => void runs.refetch()} />
        )}
        {runs.data?.length === 0 && <p className="text-mute m-0 text-sm">It has not run yet.</p>}
        {runs.data?.map((run) => (
          <div key={run.id} className="border-line flex flex-col gap-0.5 border-b pb-2 text-[13px]">
            <div className="flex justify-between gap-3">
              <span className="font-mono text-[12px]">{stamp(run.startedAt)}</span>
              <span>
                {run.status} · {run.delivered} delivered
                {run.failed > 0 ? ` · ${run.failed} failed` : ''}
              </span>
            </div>
            {run.error !== undefined && <div className="text-mute text-xs">{run.error}</div>}
          </div>
        ))}
      </div>
    </Modal>
  );
}
